import { forwardRef, memo } from "react"
import type { ReactNode } from "react"

import { formatDistanceToNow } from "date-fns"

import { useDateLocale } from "@/hooks/use-date-locale"
import { cn } from "@/lib/utils"

type SidebarSearchResultItemProps = {
  className?: string
  icon?: ReactNode
  isActive?: boolean
  isUnread?: boolean
  subtitle?: string
  timestamp?: string | Date
  title: string
  onClick?: () => void
}

const SidebarSearchResultItemInner = forwardRef<HTMLButtonElement, SidebarSearchResultItemProps>(
  function SidebarSearchResultItem(
    { className, icon, isActive = false, isUnread = false, subtitle, timestamp, title, onClick },
    ref
  ) {
    const dateLocale = useDateLocale()
    const relativeTime = timestamp
      ? formatDistanceToNow(new Date(timestamp), { addSuffix: true, locale: dateLocale })
      : null

    const secondaryText = subtitle || relativeTime

    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          "group/search-item flex w-full items-start gap-3 rounded-md px-2 py-2 text-left transition-colors duration-150",
          "hover:bg-sidebar-accent hover:!text-sidebar-accent-foreground dark:hover:bg-sidebar-foreground/14 dark:hover:!text-sidebar-foreground",
          "dark:text-sidebar-foreground",
          isActive && "bg-sidebar-accent !text-sidebar-accent-foreground",
          className
        )}
        onClick={onClick}
      >
        {icon && (
          <div
            className={cn(
              "flex size-8 shrink-0 items-center justify-center rounded-full",
              "bg-muted/50 dark:bg-sidebar-foreground/12",
              isActive
                ? "bg-sidebar-accent-foreground/15 dark:bg-sidebar-accent-foreground/15"
                : "dark:text-sidebar-foreground"
            )}
          >
            {icon}
          </div>
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-sm font-medium">{title}</span>
            {isUnread && (
              <span
                aria-label="Unread response"
                className="bg-primary size-2 shrink-0 rounded-full"
                data-testid="sidebar-search-result-unread-indicator"
              />
            )}
          </div>
          {secondaryText && (
            <span
              className={cn(
                "text-xs",
                isActive
                  ? "text-sidebar-accent-foreground/90"
                  : "text-muted-foreground dark:text-sidebar-foreground/90 dark:group-hover/search-item:text-sidebar-foreground"
              )}
            >
              {secondaryText}
            </span>
          )}
        </div>
      </button>
    )
  }
)

export const SidebarSearchResultItem = memo(SidebarSearchResultItemInner)
